<script setup lang="ts">
import { useField } from "vee-validate";
import { computed, useAttrs, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { capitalizeFirstLetter } from "@/utils/Capitalize.ts";
import { formatForElementId } from "@/utils/Formatters";
const { t } = useI18n();

const props = defineProps<{
  name?: string;
  label?: string;
  description?: string;
  defaultLabel?: boolean;
  density?: "default" | "comfortable" | "compact";
  size?: "small";
  color?: string;
  trueValue?: any;
  isCheckedCallback?: (value: any) => boolean;
  falseValue?: any;
  valueComparator?: (current: any, target: any) => boolean;
  disabled?: boolean;
  rounded?: boolean;
  required?: boolean;
  optionValue?: any;
  exclusiveOptions?: any | any[];
  class?: string;
  modelValue?: any;
  hideErrorMessage?: boolean;
}>();

const isEqual = (a: any, b: any) => (props.valueComparator ? props.valueComparator(a, b) : a === b);

const trueVal = computed(() => props.trueValue ?? true);
const falseVal = computed(() => props.falseValue ?? false);

const emit = defineEmits<{
  (e: "update:modelValue", v: any): void;
}>();

const { value, setValue, setTouched, errorMessage } = useField(() => props.name ?? "");
const usesLocalModel = computed(() => !props.name);
const isArrayField = computed(() => Array.isArray(value.value));
const currentArray = () => (isArrayField.value ? (value.value as any[]) : []);

const exclusiveList = computed<any[]>(() => {
  if (Array.isArray(props.exclusiveOptions)) return props.exclusiveOptions;
  if (props.exclusiveOptions !== undefined && props.exclusiveOptions !== null)
    return [props.exclusiveOptions];
  return [];
});
const isExclusive = (v: any) =>
  exclusiveList.value.length > 0 && exclusiveList.value.some((x) => isEqual(x, v));

const withoutExclusives = (arr: any[]) => arr.filter((x) => !isExclusive(x));

const isChecked = computed(() => {
  if (usesLocalModel.value) {
    if (props.isCheckedCallback) return props.isCheckedCallback(props.modelValue);
    return isEqual(props.modelValue, trueVal.value);
  }
  if (isArrayField.value) {
    const arr = currentArray();
    return arr.some((item) => isEqual(item, props.optionValue));
  }
  if (props.isCheckedCallback) return props.isCheckedCallback(value.value);
  return isEqual(value.value, trueVal.value);
});

const innerState = ref<boolean>(isChecked.value);

watch(isChecked, (val) => (innerState.value = val));
watch(
  () => props.modelValue,
  (val) => {
    if (usesLocalModel.value) {
      innerState.value = isEqual(val, trueVal.value);
    }
  }
);

const fallthroughAttrs = useAttrs();

const humanizeErrorMessage = computed(() => {
  if (props.optionValue) {
    return capitalizeFirstLetter(errorMessage?.value ?? "");
  }
  if (props.name && errorMessage.value && props.label) {
    const fieldName = props.name.replace(/\.(\d+)\./g, "[$1].").replace(/\.(\d+)$/, "[$1]");
    return errorMessage.value.replace(fieldName, props.label ?? "");
  }
  return "";
});

function onToggle() {
  if (props.disabled) return;

  if (!usesLocalModel.value && isArrayField.value) {
    const arr = currentArray();
    const idx = arr.findIndex((item) => isEqual(item, props.optionValue));
    const togglingExclusive = isExclusive(props.optionValue);
    let next: any[];

    if (togglingExclusive) {
      // Selecting an exclusive: keep only it. Unselecting it is allowed (empty selection).
      if (idx === -1) {
        next = [props.optionValue];
        innerState.value = true;
      } else {
        next = [];
        innerState.value = false;
      }
    } else {
      // Toggling a non-exclusive
      if (idx === -1) {
        // selecting non-exclusive: remove any exclusives
        next = [...withoutExclusives(arr), props.optionValue];
        innerState.value = true;
      } else {
        // removing it — empty selection is allowed
        next = [...arr.slice(0, idx), ...arr.slice(idx + 1)];
        innerState.value = false;
      }
    }

    setValue(next);
    setTouched(true);
    emit("update:modelValue", next);
    return;
  }

  if (!usesLocalModel.value && !isArrayField.value) {
    const newValue = innerState.value ? falseVal.value : trueVal.value;
    innerState.value = isEqual(newValue, trueVal.value);
    setValue(newValue);
    setTouched(true);
    emit("update:modelValue", newValue);
    return;
  }

  const newLocal = innerState.value ? falseVal.value : trueVal.value;
  innerState.value = isEqual(newLocal, trueVal.value);
  emit("update:modelValue", newLocal);
}

defineExpose({ onToggle });
const elementId = computed(() => formatForElementId(props.label || props.name || ""));

const descriptionId = computed(() =>
  props.description ? `${elementId.value}-description` : undefined
);
</script>

<template>
  <div
    :class="[
      `${!props.disabled && 'tw:cursor-pointer'} tw:select-none tw:inline-flex tw:flex-row tw:items-center tw:!p-1 tw:gap-x-2`,
      {
        'tw:text-primary': usesLocalModel ? innerState : value,
        'tw:text-[#A3A8AB]': usesLocalModel ? !innerState : !value,
      },
      props.class ?? '',
    ]"
    role="checkbox"
    :aria-checked="innerState"
    :aria-disabled="disabled || undefined"
    :aria-label="props.label"
    :aria-describedby="descriptionId"
    @click="onToggle"
    v-bind="fallthroughAttrs"
  >
    <div
      class="tw:relative tw:shrink-0 tw:scale-75"
      data-testid="checkbox-check"
      :data-test-elementid="elementId"
    >
      <label class="tw:flex tw:items-center">
        <div
          :class="`tw:relative tw:w-6 tw:h-6 tw:border-3 tw:flex tw:items-center tw:justify-center tw:rounded-md ${disabled ? 'tw:border-[#a3a8ab]' : 'tw:border-secondary'}`"
        >
          <div
            data-testid="checkbox-checked-indicator"
            :data-test-elementid="elementId + '-checked-indicator'"
            v-if="innerState"
            :class="[
              'tw:absolute tw:w-4.5 tw:h-4.5 tw:border-3 tw:border-surface tw:rounded-md',
              {
                'tw:bg-[#a3a8ab]': disabled,
                'tw:bg-secondary tw:cursor-pointer': !disabled,
              },
            ]"
          ></div>
        </div>
      </label>
    </div>

    <div class="tw:flex tw:min-w-0 tw:flex-col tw:gap-y-0">
      <span
        v-if="props.label"
        :class="[
          'tw:font-medium tw:max-sm:text-sm tw:md:text-md',
          {
            'tw:text-primary': !errorMessage,
            'tw:!text-xs': props.size === 'small' && !props.description,
            'tw:!text-sm': props.size === 'small' && !!props.description,
          },
        ]"
      >
        {{ props.label ?? t(`generic.fields.${props.name}`) }}
        <span class="tw:!text-secondary">{{ props.required ? "*" : "" }}</span>
      </span>
      <span
        v-else-if="props.defaultLabel"
        :class="['tw:text-md tw:font-medium', { 'tw:text-primary': !errorMessage }]"
      >
        {{
          innerState
            ? t("generic.components.checkBoxField.defaultTrue")
            : t("generic.components.checkBoxField.defaultFalse")
        }}
      </span>
      <div
        v-if="props.description"
        :id="descriptionId"
        class="tw:text-xxs tw:font-normal tw:leading-snug tw:text-medium-emphasis"
      >
        {{ props.description }}
      </div>
    </div>
  </div>
  <div
    v-if="errorMessage && !hideErrorMessage"
    class="tw:text-xxs tw:text-error tw:!ml-1"
    data-testid="checkbox-error-message"
  >
    {{ humanizeErrorMessage }}
  </div>
</template>
